<template>
  <ElDrawer
    v-model="visible"
    :title="title"
    :size="size"
    :direction="direction"
    :modal="modal"
    :modal-class="modalClass"
    :append-to-body="appendToBody"
    :lock-scroll="lockScroll"
    :custom-class="customClass"
    :open-delay="openDelay"
    :close-delay="closeDelay"
    :close-on-click-modal="closeOnClickModal"
    :close-on-press-escape="closeOnPressEscape"
    :show-close="showClose"
    :before-close="handleBeforeClose"
    :destroy-on-close="destroyOnClose"
    :with-header="withHeader"
    @open="handleOpen"
    @opened="handleOpened"
    @close="handleClose"
    @closed="handleClosed"
  >
    <!-- 头部插槽 -->
    <template v-if="$slots.header" #header="{ close, titleId, titleClass }">
      <slot
        name="header"
        :close="close"
        :title-id="titleId"
        :title-class="titleClass"
      ></slot>
    </template>

    <!-- 默认内容插槽 -->
    <div class="drawer-content">
      <slot>
        <div v-if="content" class="drawer-text-content">
          {{ content }}
        </div>
      </slot>
    </div>

    <!-- 底部插槽 -->
    <template v-if="$slots.footer || showDefaultFooter" #footer>
      <slot name="footer">
        <div v-if="showDefaultFooter" class="drawer-footer">
          <ElButton @click="handleCancel">
            {{ cancelText }}
          </ElButton>
          <ElButton
            type="primary"
            :loading="confirmLoading"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </ElButton>
        </div>
      </slot>
    </template>
  </ElDrawer>
</template>

<script setup lang="ts">
/**
 * Drawer 抽屉组件
 * 基于 Element Plus Drawer 组件封装
 * @author AI Assistant
 * @date 2024-01-01
 */

import { computed } from 'vue'
import type { DrawerDirection } from 'element-plus'

// 接口定义
interface Props {
  /** 是否显示 */
  modelValue?: boolean
  /** 标题 */
  title?: string
  /** 抽屉尺寸 */
  size?: string | number
  /** 抽屉方向 */
  direction?: DrawerDirection
  /** 是否显示遮罩层 */
  modal?: boolean
  /** 遮罩层类名 */
  modalClass?: string
  /** 是否插入到 body 元素上 */
  appendToBody?: boolean
  /** 是否锁定滚动 */
  lockScroll?: boolean
  /** 自定义类名 */
  customClass?: string
  /** 打开延时 */
  openDelay?: number
  /** 关闭延时 */
  closeDelay?: number
  /** 是否可以通过点击遮罩层关闭 */
  closeOnClickModal?: boolean
  /** 是否可以通过按下 ESC 关闭 */
  closeOnPressEscape?: boolean
  /** 是否显示关闭按钮 */
  showClose?: boolean
  /** 关闭时销毁子元素 */
  destroyOnClose?: boolean
  /** 是否显示头部 */
  withHeader?: boolean
  /** 内容文本 */
  content?: string
  /** 是否显示默认底部 */
  showDefaultFooter?: boolean
  /** 取消按钮文本 */
  cancelText?: string
  /** 确认按钮文本 */
  confirmText?: string
  /** 确认按钮加载状态 */
  confirmLoading?: boolean
}

interface Emits {
  /** 更新显示状态 */
  'update:modelValue': [value: boolean]
  /** 打开事件 */
  open: []
  /** 打开后事件 */
  opened: []
  /** 关闭事件 */
  close: []
  /** 关闭后事件 */
  closed: []
  /** 取消事件 */
  cancel: []
  /** 确认事件 */
  confirm: []
}

// 定义 props
const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  title: '',
  size: '30%',
  direction: 'rtl',
  modal: true,
  modalClass: '',
  appendToBody: false,
  lockScroll: true,
  customClass: '',
  openDelay: 0,
  closeDelay: 0,
  closeOnClickModal: true,
  closeOnPressEscape: true,
  showClose: true,
  destroyOnClose: false,
  withHeader: true,
  content: '',
  showDefaultFooter: false,
  cancelText: '取消',
  confirmText: '确定',
  confirmLoading: false
})

// 定义 emits
const emit = defineEmits<Emits>()

// 内部显示状态
const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    emit('update:modelValue', value)
  }
})

/**
 * 关闭前处理函数
 */
const handleBeforeClose = (done: () => void): void => {
  // 可以在这里添加关闭前的确认逻辑
  done()
}

/**
 * 打开事件处理
 */
const handleOpen = (): void => {
  emit('open')
}

/**
 * 打开后事件处理
 */
const handleOpened = (): void => {
  emit('opened')
}

/**
 * 关闭事件处理
 */
const handleClose = (): void => {
  emit('close')
}

/**
 * 关闭后事件处理
 */
const handleClosed = (): void => {
  emit('closed')
}

/**
 * 取消按钮点击处理
 */
const handleCancel = (): void => {
  visible.value = false
  emit('cancel')
}

/**
 * 确认按钮点击处理
 */
const handleConfirm = (): void => {
  emit('confirm')
}

/**
 * 手动打开抽屉
 */
const open = (): void => {
  visible.value = true
}

/**
 * 手动关闭抽屉
 */
const close = (): void => {
  visible.value = false
}

// 暴露方法
defineExpose({
  open,
  close
})
</script>

<style scoped>
.drawer-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.drawer-text-content {
  padding: 20px 0;
  line-height: 1.6;
  color: var(--el-text-color-primary);
  font-size: 14px;
  flex: 1;
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 0 0;
  border-top: 1px solid var(--el-border-color-lighter);
  margin-top: auto;
}

/* 自定义样式变量 */
:deep(.el-drawer) {
  --el-drawer-border-radius: 0;
}

:deep(.el-drawer__header) {
  padding: 20px 20px 16px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  margin-bottom: 0;
}

:deep(.el-drawer__body) {
  padding: 20px;
  display: flex;
  flex-direction: column;
  height: calc(100% - 60px);
}

:deep(.el-drawer__close-btn) {
  font-size: 18px;
  color: var(--el-text-color-regular);
}

:deep(.el-drawer__close-btn:hover) {
  color: var(--el-text-color-primary);
}

/* 不同方向的样式调整 */
:deep(.el-drawer.ltr) {
  border-radius: 0 8px 8px 0;
}

:deep(.el-drawer.rtl) {
  border-radius: 8px 0 0 8px;
}

:deep(.el-drawer.ttb) {
  border-radius: 0 0 8px 8px;
}

:deep(.el-drawer.btt) {
  border-radius: 8px 8px 0 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  :deep(.el-drawer) {
    width: 100% !important;
  }

  :deep(.el-drawer__body) {
    padding: 15px;
  }

  .drawer-footer {
    flex-direction: column-reverse;
    gap: 8px;
  }

  .drawer-footer .el-button {
    width: 100%;
  }
}

/* 暗色主题适配 */
@media (prefers-color-scheme: dark) {
  :deep(.el-drawer) {
    background-color: var(--el-bg-color-page);
  }
}
</style>
